import React, { useEffect, useMemo, useState } from "react";
import { fileURL } from "../../../utils/fileURL";
import { renderTextoRico } from "../../../utils/textoRico";
import { EtiquetaPreview } from "./EtiquetaProducto";
import BarcodeLabelsModal from "./BarcodeLabelsModal";
import { formatoFechaSimple } from "../../../utils/formatoFecha";
import "./ProductViewModal.css";

/**
 * Ficha de producto.
 *
 * La informacion esta ordenada por lo que se consulta primero: arriba el
 * nombre y el estado, despues los tres numeros que se miran de un vistazo
 * (disponible, vendido, precio), y al final los datos de referencia.
 */

/**
 * Un dato de la cabecera: el valor en una pastilla y debajo que cosa es.
 *
 * Mismo tratamiento que en el listado: quien viene de la lista encuentra la
 * misma forma aca, y no tiene que volver a aprender donde mirar.
 */
const Chip = ({ valor, etiqueta }) => {
  if (valor === null || valor === undefined || valor === "") return null;

  return (
    <span className="pv-chip">
      <span className="pv-chip-valor">{valor}</span>
      <span className="pv-chip-label">{etiqueta}</span>
    </span>
  );
};

const moneyOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatoUSD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(n);

const formatoFecha = (valor) => {
  if (!valor) return null;
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

function ProductViewModal({ isOpen, onClose, product }) {
  const [showEtiqueta, setShowEtiqueta] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const disponible = Number(product?.cantidad_disponible ?? 0);
  const vendido = Number(product?.cantidad_vendida ?? 0);

  const stockTone = useMemo(() => {
    if (disponible === 0) return "danger";
    if (disponible < 5) return "warn";
    return "ok";
  }, [disponible]);

  const imageUrl = useMemo(
    () => (product?.url_image ? fileURL(product.url_image) : null),
    [product?.url_image]
  );

  const pdfUrl = useMemo(
    () => (product?.url_documento ? fileURL(product.url_documento) : null),
    [product?.url_documento]
  );

  const pdfName = useMemo(() => {
    const raw = product?.url_documento;
    if (!raw) return "";
    return String(raw).split("/").pop() || "document.pdf";
  }, [product?.url_documento]);

  const priceValue = useMemo(
    () => moneyOrNull(product?.precio_unitario),
    [product?.precio_unitario]
  );
  const coreChargeValue = useMemo(
    () => moneyOrNull(product?.core_charge),
    [product?.core_charge]
  );

  const unidad =
    product?.unidad_abreviatura || product?.unidad_nombre || "";

  // Proporcion vendido / (vendido + disponible), para la barra de inventario.
  const totalMovido = disponible + vendido;
  const pctVendido = totalMovido > 0 ? Math.round((vendido / totalMovido) * 100) : 0;

  if (!isOpen || !product) return null;

  const activo = Number(product.estado) === 1;

  const handleBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div className="pv-overlay" onMouseDown={handleBackdropMouseDown}>
      <div className="pv-modal" onMouseDown={(e) => e.stopPropagation()}>
        {/* ---------- Cabecera: el nombre es el titulo ---------- */}
        <div className="pv-header">
          <div className="pv-header-left">
            <h2 className="pv-title" title={product.nombre || ""}>
              {product.nombre || "Untitled product"}
            </h2>

            <div className="pv-chips">
              <Chip valor={product.no_parte} etiqueta="Part number" />
              <Chip valor={product.numero_serie} etiqueta="Serial number" />
              <Chip valor={product.ubicacion} etiqueta="Location" />
              <Chip valor={product.condicion_nombre} etiqueta="Condition" />
              <Chip valor={product.certificado_nombre} etiqueta="Certificate" />
              <Chip
                valor={formatoFechaSimple(product.fecha_certificado)}
                etiqueta="Certificate date"
              />
              <Chip valor={product.producto_id} etiqueta="ID" />

              <span className={`pv-estado pv-estado--${activo ? "on" : "off"}`}>
                <i aria-hidden="true" />
                {activo ? "Active" : "Inactive"}
              </span>
            </div>
          </div>

          <button className="pv-close" onClick={onClose} aria-label="Close">
            &#10005;
          </button>
        </div>

        <div className="pv-body">
          {/* ---------- Los tres numeros que se miran primero ---------- */}
          <div className="pv-resumen">
            <div className={`pv-kpi pv-kpi--${stockTone}`}>
              <span className="pv-kpi-label">Available</span>
              <span className="pv-kpi-valor">{disponible}</span>
              <span className="pv-kpi-nota">
                {disponible === 0
                  ? "Out of stock"
                  : disponible < 5
                  ? "Low stock"
                  : "In stock"}
              </span>
            </div>

            <div className="pv-kpi">
              <span className="pv-kpi-label">Sold</span>
              <span className="pv-kpi-valor">{vendido}</span>
              <span className="pv-kpi-nota">
                {totalMovido > 0 ? `${pctVendido}% of all units` : "No movement yet"}
              </span>
            </div>

            <div className="pv-kpi">
              <span className="pv-kpi-label">Unit price</span>
              <span className="pv-kpi-valor">
                {priceValue === null ? "---" : formatoUSD(priceValue)}
              </span>
              <span className="pv-kpi-nota">
                {coreChargeValue ? `+ ${formatoUSD(coreChargeValue)} core` : "No core charge"}
              </span>
            </div>
          </div>

          {/* Reparto visual de inventario: cuanto queda vs cuanto salio. */}
          {totalMovido > 0 ? (
            <div className="pv-barra">
              <div className="pv-barra-pista">
                <div
                  className={`pv-barra-relleno pv-barra-relleno--${stockTone}`}
                  style={{ width: `${100 - pctVendido}%` }}
                />
              </div>
              <div className="pv-barra-leyenda">
                <span>
                  <i className={`pv-punto pv-punto--${stockTone}`} aria-hidden="true" />
                  {disponible} available
                </span>
                <span>
                  <i className="pv-punto pv-punto--gris" aria-hidden="true" />
                  {vendido} sold
                </span>
              </div>
            </div>
          ) : null}

          {/* Tres columnas y no dos: con dos, la de la izquierda juntaba
              imagen + codigo + documento y quedaba mucho mas alta que la
              otra, asi que la ficha scrolleaba aunque sobrara ancho. */}
          <div className="pv-columnas">
            {/* ---------- 1: la pieza ---------- */}
            <div className="pv-col">
              <div className="pv-card">
                <div className="pv-card-head">
                  <span className="pv-card-title">Image</span>
                </div>

                <div className="pv-image-wrap">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={product.nombre || "Product"}
                      className="pv-image"
                    />
                  ) : (
                    <div className="pv-empty">
                      <div className="pv-empty-txt">No image available</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="pv-card">
                <div className="pv-card-head">
                  <span className="pv-card-title">Barcode</span>

                  {product.codigo_barra ? (
                    <button
                      type="button"
                      className="pv-btn-primary"
                      onClick={() => setShowEtiqueta(true)}
                    >
                      Print label
                    </button>
                  ) : null}
                </div>

                {product.codigo_barra ? (
                  /* La etiqueta entera, no solo las barras: lo que se ve
                     aca es lo que sale de la impresora, asi no hay que
                     abrir el dialogo de impresion para saber como quedo. */
                  <div className="pv-barcode">
                    <EtiquetaPreview producto={product} />
                  </div>
                ) : (
                  <div className="pv-doc-empty">
                    No barcode. Only products created after this feature was
                    added have one.
                  </div>
                )}
              </div>

            </div>

            {/* ---------- 2: los datos ---------- */}
            <div className="pv-col">
              <div className="pv-card">
                <div className="pv-card-head">
                  <span className="pv-card-title">Classification</span>
                </div>

                <dl className="pv-datos">
                  <Dato label="Category" valor={product.categoria_nombre} />
                  <Dato label="Brand" valor={product.marca_nombre} />

                  {/* Condicion y certificado van con la clasificacion, no
                      con precios: describen la pieza, y en aviacion son de
                      lo primero que se pregunta. */}
                  {/* Condicion, certificado, serie y ubicacion ya salen
                      arriba como pastillas. Repetirlos aca seria leer dos
                      veces lo mismo en la misma pantalla. */}
                </dl>
              </div>

              <div className="pv-card">
                <div className="pv-card-head">
                  <span className="pv-card-title">Measurements &amp; pricing</span>
                </div>

                <dl className="pv-datos">
                  <Dato
                    label="Weight"
                    valor={
                      product.peso
                        ? `${product.peso}${unidad ? ` ${unidad}` : ""}`
                        : null
                    }
                  />
                  <Dato
                    label="Unit price"
                    valor={priceValue === null ? null : formatoUSD(priceValue)}
                  />
                  <Dato
                    label="Core charge"
                    valor={
                      coreChargeValue === null ? null : formatoUSD(coreChargeValue)
                    }
                  />
                  <Dato label="Created" valor={formatoFecha(product.fecha_creado)} />
                </dl>
              </div>

              <div className="pv-card">
                <div className="pv-card-head">
                  <span className="pv-card-title">Document</span>
                </div>

                {pdfUrl ? (
                  <div className="pv-doc-row">
                    <div className="pv-doc-meta">
                      <div className="pv-doc-name" title={pdfName}>
                        {pdfName}
                      </div>
                      <div className="pv-doc-hint">PDF attached</div>
                    </div>

                    <a
                      className="pv-btn-primary"
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open
                    </a>
                  </div>
                ) : (
                  <div className="pv-doc-empty">No PDF uploaded</div>
                )}
              </div>
            </div>

            {/* ---------- 3: la descripcion, que es la que gana con el
                     ancho: un texto largo en una columna angosta se corta
                     cada pocas palabras. ---------- */}
            <div className="pv-col">
              <div className="pv-card pv-card--crece">
                <div className="pv-card-head">
                  <span className="pv-card-title">Description</span>
                </div>

                <div className="pv-desc">
                  {renderTextoRico(product.descripcion) || (
                    <span className="pv-muted">No description</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pv-footer">
          <button className="pv-btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {showEtiqueta && (
        <BarcodeLabelsModal
          isOpen={showEtiqueta}
          productos={[product]}
          onClose={() => setShowEtiqueta(false)}
        />
      )}
    </div>
  );
}

function Dato({ label, valor }) {
  const vacio =
    valor === null ||
    valor === undefined ||
    (typeof valor === "string" && !valor.trim());

  return (
    <div className="pv-dato">
      <dt className="pv-dato-label">{label}</dt>
      <dd className="pv-dato-valor">
        {vacio ? <span className="pv-muted">---</span> : valor}
      </dd>
    </div>
  );
}

export default ProductViewModal;
